import { BaseApiClient } from './base-client'
import {
  API_ROUTES,
  interpolateRoute,
  type AgentTrigger,
  type ApiResponse,
  type Trigger,
  type CreateTriggerDto,
  type UpdateTriggerDto,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'

/** Request body for create (companyId comes from session or query). */
type CreateTriggerBody = Omit<CreateTriggerDto, 'companyId'>

export class TriggersApiClient extends BaseApiClient {
  async getTriggers(): Promise<AgentTrigger[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<AgentTrigger[]>>(
          API_ROUTES.TRIGGERS.GET_ALL,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching triggers',
      },
    )
    return result ?? []
  }

  async getTriggersForCompanyByAdmin(
    companyId: string,
  ): Promise<AgentTrigger[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<AgentTrigger[]>>(
          `${API_ROUTES.ADMIN.TRIGGERS.GET_ALL}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching triggers for company',
      },
    )
    return result ?? []
  }

  // --- Trigger definitions (company-scoped: resolves the caller's company) ---

  async getDefinitions(): Promise<Trigger[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Trigger[]>>(
          API_ROUTES.TRIGGERS.GET_DEFINITIONS,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching trigger definitions',
      },
    )
    return result ?? []
  }

  async createTrigger(dto: CreateTriggerBody): Promise<Trigger | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Trigger>>(
          API_ROUTES.TRIGGERS.CREATE,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating trigger' },
    )
  }

  async updateTrigger(
    id: string,
    dto: UpdateTriggerDto,
  ): Promise<Trigger | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Trigger>>(
          interpolateRoute(API_ROUTES.TRIGGERS.UPDATE, { id }),
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error updating trigger' },
    )
  }

  async deleteTrigger(id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        await this.request(
          interpolateRoute(API_ROUTES.TRIGGERS.DELETE, { id }),
          {
            method: 'DELETE',
          },
        )
        return true
      },
      { errorContext: 'Error deleting trigger' },
    )
    return result === true
  }

  // --- Trigger definitions (super-data-admin, any company) ---

  async getDefinitionsForCompanyByAdmin(companyId: string): Promise<Trigger[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Trigger[]>>(
          `${API_ROUTES.ADMIN.TRIGGERS.GET_DEFINITIONS}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching trigger definitions for company',
      },
    )
    return result ?? []
  }

  async createTriggerForCompany(
    companyId: string,
    dto: CreateTriggerBody,
  ): Promise<Trigger | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Trigger>>(
          `${API_ROUTES.ADMIN.TRIGGERS.CREATE}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating trigger' },
    )
  }

  async updateTriggerForCompany(
    id: string,
    companyId: string,
    dto: UpdateTriggerDto,
  ): Promise<Trigger | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Trigger>>(
          `${interpolateRoute(API_ROUTES.ADMIN.TRIGGERS.UPDATE, { id })}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error updating trigger' },
    )
  }

  async deleteTriggerForCompany(
    id: string,
    companyId: string,
  ): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        await this.request(
          `${interpolateRoute(API_ROUTES.ADMIN.TRIGGERS.DELETE, { id })}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'DELETE' },
        )
        return true
      },
      { errorContext: 'Error deleting trigger' },
    )
    return result === true
  }
}

export const triggersApiClient = new TriggersApiClient()
